import { useState } from 'react';
import { Stack, Button, CircularProgress, Tooltip } from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  VisibilityOutlined as ViewIcon,
  CloseOutlined as CloseIcon,
} from '@mui/icons-material';

import { useDownloadFileMutation, useLazyViewFileQuery } from '../file-storage/api/fileStorageApi';
import { downloadFromResponse } from '../file-storage/utils/fileStorageHelpers';
import { useContainerNarrow } from './hooks/useContainerNarrow';
import { useRef } from 'react';

type FileFieldActionsProps = {
  fileGuid: string;
  fallbackName?: string;
  onDelete?: () => void;
  disabled?: boolean;
  /**
   * When true the component renders as `width: fit-content` so it doesn't
   * inherit the full parent width (detail form context).
   * When false (default) it stretches to fill the parent (grid cell context).
   */
  fitContent?: boolean;
};

// Below this px width the buttons collapse to icon-only
const NARROW_THRESHOLD = 260;

export const FileFieldActions = ({
  fileGuid,
  fallbackName = 'file',
  onDelete,
  disabled,
  fitContent = false,
}: FileFieldActionsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isNarrow = useContainerNarrow(wrapperRef, NARROW_THRESHOLD);

  const [downloadFile, { isLoading: isDownloading }] = useDownloadFileMutation();
  const [failedAction, setFailedAction] = useState<'download' | 'view' | null>(null);
  const [triggerView] = useLazyViewFileQuery();

  const handleView = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setFailedAction(null);

    const viewRequest = triggerView({ key: fileGuid });

    try {
      const blob = await viewRequest.unwrap();
      const url = URL.createObjectURL(blob);
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');

      if (!newTab) {
        URL.revokeObjectURL(url);
        setFailedAction('view');
      } else {
        newTab.opener = null;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch {
      setFailedAction('view');
    } finally {
      viewRequest.unsubscribe();
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setFailedAction(null);
    try {
      const res = await downloadFile({ key: fileGuid }).unwrap();
      if (!downloadFromResponse(res, fallbackName)) setFailedAction('download');
    } catch {
      setFailedAction('download');
    }
  };

  const iconOnlySx = { '& .MuiButton-startIcon': { mr: 0 } } as const;

  // In fitContent mode: wrapper div shrinks to content → ResizeObserver sees real width.
  // In fill mode (grid cells): wrapper stretches → ResizeObserver detects cell narrowing.
  return (
    <div
      ref={wrapperRef}
      style={{
        display: fitContent ? 'inline-flex' : 'flex',
        width: fitContent ? 'fit-content' : '100%',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={0} sx={{ width: '100%', height: '100%', minWidth: 0 }}>
        <Tooltip title="Download">
          <span style={{ flex: fitContent ? undefined : 1, display: 'flex' }}>
            <Button
              size="small"
              variant="outlined"
              color={failedAction === 'download' ? 'error' : 'success'}
              startIcon={
                isDownloading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
              onClick={handleDownload}
              disabled={disabled || isDownloading}
              sx={{
                flex: fitContent ? undefined : 1,
                borderRadius: 0,
                textTransform: 'none',
                height: '100%',
                minWidth: 0,
                ...(isNarrow && iconOnlySx),
              }}
            >
              {!isNarrow && 'Download'}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="View">
          <span style={{ flex: fitContent ? undefined : 1, display: 'flex' }}>
            <Button
              size="small"
              variant="outlined"
              color={failedAction === 'view' ? 'error' : 'primary'}
              startIcon={<ViewIcon fontSize="small" />}
              onClick={handleView}
              disabled={disabled}
              sx={{
                flex: fitContent ? undefined : 1,
                borderRadius: 0,
                textTransform: 'none',
                height: '100%',
                minWidth: 0,
                ...(isNarrow && iconOnlySx),
              }}
            >
              {!isNarrow && 'View'}
            </Button>
          </span>
        </Tooltip>

        {onDelete && (
          <Tooltip title="Delete">
            <span style={{ display: 'flex' }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                disabled={disabled}
                sx={{
                  borderRadius: 0,
                  textTransform: 'none',
                  height: '100%',
                  minWidth: 0,
                  px: 1,
                }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </div>
  );
};
